import React, { memo, useCallback } from 'react';
import { areEqual } from 'react-window';
import styled, { css } from 'styled-components';
import { TreeNode as CldTreeNode, ShowMore as CldShowMore } from '@cld/tree';
import translatePredefinedName from '../../utils/translatePredefinedName';
import { types as ats } from '../../actions';
import TreeNodeWithIcons from './TreeNodeWithIcons';

const depthCss = css`
  padding-left: ${({ depth }) => depth * 20}px;
`;

const TreeNode = styled(CldTreeNode)`
  ${depthCss}
`;

const ShowMore = styled(CldShowMore)`
  ${depthCss}
`;

const { LOAD_TREE_FOLDERS, SET_NAVIGATION_CURRENT_FOLDER, CLEAR_SEARCH_TERM } = ats;

// styling for horizontal scroll - should be temp and solved in cld/tree once get a design from UX
const ListItemWrapper = styled.span`
  min-width: 100% !important;
  width: auto !important;
  padding-right: 5px;

  span {
    line-height: normal;
    text-align: start;
  }
`;

const Node = memo(({ index, style, data: { nodes, actions, folders } }) => {
  const node = nodes[index];
  const { name, renderedNodeId, nodeId, origNodeId, ...treeNodeProps } = nodes[index];

  const { id: folderId, kind: folderKind, driveId: folderDriveId, path: folderPath, treePath: folderTreePath } = folders[nodeId] || {};

  // used by the load more button in the tree -
  // since node can be attached in a different location of the tree need to get the original node id that generated it.
  const { id: origFolderId, path: origFolderPath, kind: origFolderKind, driveId: origDriveId, cursor: origFolderCursor } = folders[origNodeId] || {};

  const onExpand = useCallback(() => {
    actions[LOAD_TREE_FOLDERS]({
      id: folderId,
      path: folderPath,
      kind: folderKind,
      driveId: folderDriveId,
    });
  }, [actions, folderId, folderPath, folderKind, folderDriveId]);

  const onMore = useCallback(() => {
    actions[LOAD_TREE_FOLDERS]({
      id: origFolderId,
      path: origFolderPath,
      kind: origFolderKind,
      driveId: origDriveId,
      cursor: origFolderCursor,
    });
  }, [actions, origFolderId, origFolderPath, origFolderKind, origDriveId, origFolderCursor]);

  const onSelect = useCallback(() => {
    actions[SET_NAVIGATION_CURRENT_FOLDER]({
      id: folderId,
      path: folderTreePath || folderPath,
      kind: folderKind,
      driveId: folderDriveId,
    });

    actions[CLEAR_SEARCH_TERM]();
  }, [actions, folderId, folderPath, folderTreePath, folderKind, folderDriveId]);

  const children = useCallback((props) => <TreeNodeWithIcons {...props} />, []);

  return (
    <ListItemWrapper style={style}>
      {renderedNodeId ? (
        <ShowMore {...node} nodeId={nodeId} onClick={!node.progress ? onMore : undefined} size="sm" />
      ) : (
        <TreeNode
          onExpand={!node.isAlreadyExpanded ? onExpand : undefined}
          onSelect={onSelect}
          nodeId={nodeId}
          name={translatePredefinedName(treeNodeProps.kind, name)}
          size="sm"
          {...treeNodeProps}
        >
          {children}
        </TreeNode>
      )}
    </ListItemWrapper>
  );
}, areEqual);

export default Node;
